<template>
  <div class="h-full flex flex-col min-h-0">
    <!-- Nothing open -->
    <div v-if="!company && !loading && !error" class="flex-1 flex flex-col items-center justify-center text-center px-6">
      <p class="font-semibold text-gray-700 dark:text-gray-200">Select a company</p>
      <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">Pick one on the left to see what is known about it.</p>
    </div>

    <!-- Loading -->
    <div v-else-if="loading && !company" class="flex-1 animate-pulse">
      <div class="max-w-3xl mx-auto px-5 sm:px-6">
        <div class="flex gap-3.5 pt-5 pb-5">
          <div class="w-11 h-11 rounded-xl bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>
          <div class="flex-1 space-y-2.5 pt-0.5"><div class="h-5 w-2/3 rounded bg-gray-200 dark:bg-gray-700"></div><div class="h-3.5 w-1/3 rounded bg-gray-100 dark:bg-gray-800"></div></div>
        </div>
        <hr class="border-gray-200 dark:border-gray-700">
        <div class="py-5 space-y-3"><div v-for="n in 6" :key="n" class="h-3.5 rounded bg-gray-100 dark:bg-gray-800" :style="{ width: `${96 - n * 6}%` }"></div></div>
      </div>
    </div>

    <!-- Error -->
    <div v-else-if="error && !company" class="flex-1 flex flex-col items-center justify-center text-center px-6">
      <p class="font-semibold text-gray-700 dark:text-gray-200">{{ error }}</p>
      <button type="button" @click="$emit('retry')" class="mt-3 text-sm font-semibold text-blue-600 dark:text-blue-400 hover:underline">Try again</button>
    </div>

    <!-- The company -->
    <div v-else-if="company" ref="scroller" @scroll.passive="onScroll" class="flex-1 min-h-0 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      <!-- The company's top section stays in view while the rest scrolls under it -->
      <div class="sticky top-0 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700" :class="compact ? 'shadow-sm' : 'shadow-none'" style="transition: box-shadow 300ms ease-out">
       <div class="max-w-3xl mx-auto px-5 sm:px-6">
        <button v-if="showBack" type="button" @click="$emit('back')"
          class="lg:hidden inline-flex items-center gap-1 pt-4 text-sm font-semibold text-blue-600 dark:text-blue-400">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5"/></svg>
          Companies
        </button>

        <!-- Shrinks once the page is scrolled, with a short animation: the logo scales down, the name gets smaller, and the
             tagline and links fold away, so more of the page is left to read. Nothing moves for people who prefer less motion. -->
        <header class="flex items-start gap-3.5 transition-[padding] duration-300 ease-out motion-reduce:transition-none" :class="compact ? 'pt-2 pb-2' : 'pt-4 pb-4'">
          <!-- The logo really changes size (not a scaled copy), so it stays sharp and the text beside it follows -->
          <JobAvatar :name="company.name || host" :src="company.logo_url" size="lg"
            class="transition-[width,height,border-radius,font-size] duration-300 ease-out motion-reduce:transition-none"
            :class="compact ? 'w-8! h-8! rounded-lg! text-sm!' : ''" />
          <div class="min-w-0 flex-1">
            <h2 class="font-bold tracking-tight text-gray-900 dark:text-white transition-[font-size,line-height] duration-300 ease-out motion-reduce:transition-none"
              :class="compact ? 'text-base leading-8 truncate' : 'text-xl leading-7 break-words'">{{ company.name || 'Company' }}</h2>

            <!-- Folds shut by animating its row from its full height to nothing -->
            <div class="grid transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:transition-none"
              :class="compact ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100'" :aria-hidden="compact">
              <div class="min-h-0 overflow-hidden">
                <p v-if="tagline" class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{{ tagline }}</p>
                <div class="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-sm">
                  <a v-if="company.website_url" :href="company.website_url" target="_blank" rel="noopener noreferrer" data-turbo="false" :tabindex="compact ? -1 : undefined"
                    class="font-medium text-blue-600 dark:text-blue-400 hover:underline">{{ host }}</a>
                  <a v-if="company.linkedin_url" :href="company.linkedin_url" target="_blank" rel="noopener noreferrer" data-turbo="false" :tabindex="compact ? -1 : undefined"
                    class="text-gray-500 dark:text-gray-400 hover:underline">LinkedIn</a>
                  <a v-for="link in socialLinks" :key="link.network" :href="link.url" target="_blank" rel="noopener noreferrer" data-turbo="false" :tabindex="compact ? -1 : undefined"
                    class="text-gray-500 dark:text-gray-400 hover:underline capitalize">{{ link.network }}</a>
                </div>
              </div>
            </div>
          </div>
          <div v-if="swatches.length" class="flex items-center gap-1 flex-shrink-0 mt-1 transition-[opacity,transform] duration-300 ease-out motion-reduce:transition-none"
            :class="compact ? 'opacity-0 scale-75 pointer-events-none' : 'opacity-100 scale-100'" role="img" :aria-label="`Brand colours: ${swatches.join(', ')}`">
            <span v-for="hex in swatches" :key="hex" class="w-4 h-4 rounded-full ring-1 ring-black/10 dark:ring-white/20" :style="{ backgroundColor: hex }" :title="hex"></span>
          </div>
        </header>
       </div>
      </div>

      <div class="max-w-3xl mx-auto px-5 sm:px-6 pb-10">
        <!-- Whether this company hires for itself or is a recruiter posting for others. It decides what the Employer filter on Job Listings shows. -->
        <div class="flex flex-wrap items-center gap-2 pt-4">
          <span class="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">Type</span>
          <FilterDropdown label="Type" :options="KIND_OPTIONS" :model-value="company.kind || 'company'" neutral="" :disabled="savingKind" @update:model-value="changeKind" />
          <span v-if="kindError" class="text-xs text-red-600 dark:text-red-400" role="alert">{{ kindError }}</span>

          <!-- Blocking hides this company's jobs from Job Listings. It asks first, in place (a dialog would open behind this panel). -->
          <div class="ml-auto flex items-center gap-2">
            <template v-if="!blocked && confirmingBlock">
              <span class="text-xs text-gray-500 dark:text-gray-400">Hide all its jobs?</span>
              <button type="button" @click="setBlocked(true)" :disabled="savingBlock"
                class="h-8 px-3 rounded-lg bg-red-600 hover:bg-red-700 text-[13px] font-semibold text-white disabled:opacity-60 transition-colors">Block</button>
              <button type="button" @click="confirmingBlock = false" :disabled="savingBlock"
                class="h-8 px-3 rounded-lg text-[13px] font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">Cancel</button>
            </template>
            <button v-else-if="!blocked" type="button" @click="confirmingBlock = true"
              class="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-gray-200 dark:border-gray-700 text-[13px] font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M18.364 18.364A9 9 0 0 0 5.636 5.636m12.728 12.728A9 9 0 0 1 5.636 5.636m12.728 12.728L5.636 5.636"/></svg>
              Block company
            </button>
          </div>
        </div>

        <!-- A blocked company -->
        <div v-if="blocked" class="mt-3 flex items-center gap-3 rounded-xl border border-red-200 dark:border-red-900/60 bg-red-50 dark:bg-red-900/15 px-3.5 py-2.5" role="status">
          <p class="min-w-0 flex-1 text-[13px] text-red-800 dark:text-red-200">
            <strong class="font-semibold">Blocked.</strong> Its jobs are hidden from Job Listings. They stay saved, and come back when you unblock it.
          </p>
          <button type="button" @click="setBlocked(false)" :disabled="savingBlock"
            class="flex-shrink-0 h-8 px-3 rounded-lg bg-white dark:bg-[#1C2128] border border-red-200 dark:border-red-900/60 text-[13px] font-semibold text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-60 transition-colors">Unblock</button>
        </div>
        <hr class="mt-4 border-gray-200 dark:border-gray-700">

        <!-- Not fetched yet -->
        <p v-if="!company.linkedin" class="py-5 text-sm text-gray-500 dark:text-gray-400">
          The details of this company have not been fetched yet. The extension fetches them after the jobs, or you can press
          <span class="font-semibold">Fetch companies</span> in the extension.
        </p>

        <template v-else>
          <section class="py-5" aria-labelledby="company-about">
            <h3 id="company-about" class="text-sm font-semibold text-gray-900 dark:text-white mb-3">About</h3>
            <dl v-if="facts.length" class="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3 mb-4">
              <div v-for="fact in facts" :key="fact.label" class="min-w-0">
                <dt class="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">{{ fact.label }}</dt>
                <dd class="text-sm text-gray-800 dark:text-gray-100 mt-0.5 break-words">{{ fact.value }}</dd>
              </div>
            </dl>
            <p v-if="company.linkedin.about" class="text-sm text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-line break-words">{{ company.linkedin.about }}</p>
            <div v-if="company.linkedin.specialties.length" class="flex flex-wrap gap-1.5 mt-4">
              <span v-for="item in company.linkedin.specialties" :key="item"
                class="px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">{{ item }}</span>
            </div>
            <p class="mt-3 text-xs text-gray-400 dark:text-gray-500">Fetched from LinkedIn {{ formatDate(company.linkedin.fetched_at) }}</p>
          </section>
        </template>

        <!-- The company's own website -->
        <template v-if="company.website_url">
          <hr class="border-gray-200 dark:border-gray-700">
          <section class="py-5" aria-labelledby="company-website">
            <h3 id="company-website" class="text-sm font-semibold text-gray-900 dark:text-white mb-3">Website</h3>
            <template v-if="company.website">
              <p v-if="company.website.title" class="text-sm font-medium text-gray-800 dark:text-gray-100">{{ company.website.title }}</p>
              <p v-if="company.website.description" class="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mt-1">{{ company.website.description }}</p>
              <ul v-if="company.website.headings.length" class="mt-3 space-y-1">
                <li v-for="heading in company.website.headings" :key="heading" class="flex gap-2.5 text-sm text-gray-600 dark:text-gray-300">
                  <span class="mt-[0.6em] w-1 h-1 rounded-full bg-gray-300 dark:bg-gray-600 flex-shrink-0" aria-hidden="true"></span>{{ heading }}
                </li>
              </ul>
              <p class="mt-3 text-xs text-gray-400 dark:text-gray-500">Read from the website {{ formatDate(company.website.fetched_at) }}</p>
            </template>
            <p v-else-if="company.website_status === 'pending'" class="text-sm text-gray-500 dark:text-gray-400">Reading the company's website...</p>
            <p v-else-if="company.website_status === 'failed'" class="text-sm text-amber-600 dark:text-amber-400">The website could not be read{{ company.website_error ? `: ${company.website_error}` : '.' }}</p>
            <p v-else class="text-sm text-gray-500 dark:text-gray-400">The website has not been read yet.</p>
          </section>
        </template>

        <hr class="border-gray-200 dark:border-gray-700">

        <!-- Its jobs -->
        <section class="py-5" aria-labelledby="company-jobs">
          <h3 id="company-jobs" class="text-sm font-semibold text-gray-900 dark:text-white mb-3">
            Jobs <span class="font-normal text-gray-400 dark:text-gray-500 tabular-nums">{{ company.jobs_count }}</span>
          </h3>
          <p v-if="!company.jobs.length" class="text-sm text-gray-500 dark:text-gray-400">No saved jobs from this company.</p>
          <ul v-else class="divide-y divide-gray-100 dark:divide-gray-800">
            <li v-for="job in company.jobs" :key="job.id" class="flex items-center gap-3 py-2">
              <div class="min-w-0 flex-1">
                <p class="text-sm font-medium text-gray-900 dark:text-white truncate">{{ job.title || 'Untitled job' }}</p>
                <p class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ [job.location, postedAgo(job.posted_at) || job.posted_text].filter(Boolean).join(' · ') }}</p>
              </div>
              <span v-if="job.applied_at" class="text-[10px] font-semibold px-1.5 rounded-full bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400">Applied</span>
              <a v-if="job.url" :href="job.url" target="_blank" rel="noopener noreferrer" data-turbo="false" title="Open on LinkedIn"
                class="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline">LinkedIn</a>
            </li>
          </ul>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import { postedAgo } from 'utils/timeAgo.js'
import { useJobCompaniesStore } from 'stores/jobCompanies.js'
import JobAvatar from './JobAvatar.vue'
import FilterDropdown from './FilterDropdown.vue'

const props = defineProps({
  company: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  error: { type: String, default: null },
  showBack: { type: Boolean, default: false },
})
defineEmits(['retry', 'back'])

// Marking the company as a recruiter or an individual company. It is saved at once, and the dropdown keeps showing what is saved when that fails.
const KIND_OPTIONS = [
  { value: 'company', label: 'Individual company' },
  { value: 'recruiter', label: 'Recruiter' },
]
const store = useJobCompaniesStore()
const savingKind = ref(false)
const kindError = ref('')

async function changeKind(kind) {
  if (kind === (props.company.kind || 'company')) return
  savingKind.value = true
  kindError.value = ''
  const result = await store.setKind(props.company.id, kind)
  savingKind.value = false
  if (!result.ok) kindError.value = result.message
}
const blocked = computed(() => Boolean(props.company?.blocked_at))
const confirmingBlock = ref(false)
const savingBlock = ref(false)

async function setBlocked(value) {
  savingBlock.value = true
  kindError.value = ''
  const result = await store.setBlocked(props.company.id, value)
  savingBlock.value = false
  confirmingBlock.value = false
  if (!result.ok) kindError.value = result.message
}
watch(() => props.company?.id, () => { kindError.value = ''; confirmingBlock.value = false })

// The header is compact once the page is scrolled. It shrinks past one distance and grows back below a smaller one, so
// the page moving under it when it changes size cannot flip it back and forth.
const COMPACT_AFTER = 64
const EXPAND_BEFORE = 12
const scroller = ref(null)
const compact = ref(false)

// The room to scroll must cover what shrinking gives back, or a short company would shrink, have nothing left to scroll,
// and grow again over and over.
const SPARE_ROOM = 100

function onScroll(event) {
  const { scrollTop: top, scrollHeight, clientHeight } = event.target
  if (!compact.value && top > COMPACT_AFTER && scrollHeight - clientHeight > COMPACT_AFTER + SPARE_ROOM) compact.value = true
  else if (compact.value && top < EXPAND_BEFORE) compact.value = false
}

// Another company opens at its top, with the full header.
watch(() => props.company?.id, async () => {
  compact.value = false
  await nextTick()
  if (scroller.value) scroller.value.scrollTop = 0
})

const host = computed(() => props.company?.website_url?.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '') || '')
const tagline = computed(() => props.company?.linkedin?.tagline || null)
const socialLinks = computed(() => (props.company?.website?.social || []).filter((link) => link.network !== 'linkedin'))

const facts = computed(() => {
  const stored = props.company?.linkedin || {}
  return [
    { label: 'Industry', value: stored.industry },
    { label: 'Size', value: stored.size },
    { label: 'Headquarters', value: stored.headquarters },
    { label: 'Founded', value: stored.founded },
    { label: 'Followers', value: stored.followers },
    { label: 'On LinkedIn', value: stored.employees_on_linkedin },
  ].filter((fact) => fact.value)
})

// The website's colour scheme: its theme colour, then the colours it names as its own, then the ones it uses most.
const swatches = computed(() => {
  const colors = props.company?.website?.colors
  if (!colors) return []
  return [...new Set([colors.theme_color, ...(colors.brand || []), ...(colors.palette || [])].filter(Boolean))].slice(0, 6)
})

function formatDate(value) {
  const date = value ? new Date(value) : null
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : ''
}
</script>
